import { css } from '@linaria/core';
import { FC, Fragment, useCallback, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import { FastMarkdown } from 'components/Markdown/FastMarkdown';
import { breakpoints } from 'styles/utils';
import { ChatInput, ChatInputRef } from './ChatInput';
import { PlaygroundMessage, useMessages } from '../store/messageStore';
import { TypingIndicator } from './TypingIndicator';
import { PlaygroundConfig } from '../types/playgroundConfig';
import { ImagePreview } from './ImagePreview';
import { ChatPlaceholder } from './ChatPlaceholder';
import { ChatMessageActionBar } from './ChatMessageActionBar';
import { usePanelContext } from './PanelContext';
interface ChatUIProps {}
const debouncedScroll = debounce((ref: HTMLDivElement) => {
  ref.scrollTo({
    top: ref.scrollHeight,
    behavior: 'smooth'
  });
}, 10);
const handleThinkTags = (markdown: string): string => {
  return markdown.replace(/<think>([\S\s]*?)<\/think>/g, (_, content: string) => {
    const lines = content.trim().split('\n');
    const quoted = lines.map(line => `> ${line}`).join('\n');
    return `\n\n> 💡 **Thought:**\n${quoted}\n\n`;
  });
};
export const ChatUI: FC<ChatUIProps> = () => {
  const panelId = usePanelContext();
  const messages = useMessages(panelId || 'default');
  const chatMessageScrollRef = useRef<HTMLDivElement>(null);
  const {
    values
  } = useFormikContext<PlaygroundConfig>();
  const chatInputRef = useRef<ChatInputRef>(null);
  useEffect(() => {
    if (chatMessageScrollRef.current) {
      debouncedScroll(chatMessageScrollRef.current);
    }
  }, [messages, values.isOutputLoading, values.isStreaming]);
  const lastMessage: PlaygroundMessage | undefined = messages[messages.length - 1];
  const onPlaceholderClick = useCallback((text: string) => {
    chatInputRef.current?.setText(text);
  }, [chatInputRef.current?.setText]);
  return <div className={chatUIContainer}>
      <div ref={chatMessageScrollRef} className={chatScrollArea}>
        {messages.length === 0 ? <ChatPlaceholder onClickHandler={onPlaceholderClick} /> : null}
        {messages.map((message, messageIndex) => {
        const isLastMessage = messageIndex === messages.length - 1;
        if (message.role === 'user') {
          return <div key={message.id} className={userChatWrapper}>
                {message.file ? <ImagePreview images={message.file} /> : null}
                <div className={userChatBubble} key={message.id}>
                  <div>{message.content}</div>
                </div>
              </div>;
        }
        return <Fragment key={message.id}>
              <div className={aiMessageWrapper}>
                <FastMarkdown className={rendererStyles} markdownText={handleThinkTags(message.error ? message.error : message.content)} />
              </div>
              <ChatMessageActionBar isLastMessage={isLastMessage} message={message} values={values} />
            </Fragment>;
      })}
        {values.isOutputLoading && !values.isStreaming || values.isStreaming && lastMessage?.role === 'user' ? <div className={modelLoadingIndicator}>
            <TypingIndicator /> <span>thinking...</span>
          </div> : null}
      </div>
      <ChatInput ref={chatInputRef} />
    </div>;
};
const chatUIContainer = "odules__Playground__Components__ChatUI_tsx-chatUIContainer-cjxagxl";
const rendererStyles = "odules__Playground__Components__ChatUI_tsx-rendererStyles-r168nik4";
const aiMessageWrapper = "odules__Playground__Components__ChatUI_tsx-aiMessageWrapper-abohjn0";
export const chatScrollArea = "odules__Playground__Components__ChatUI_tsx-chatScrollArea-cpbzty1";
const modelLoadingIndicator = "odules__Playground__Components__ChatUI_tsx-modelLoadingIndicator-m1iyo2bh";
const userChatWrapper = "odules__Playground__Components__ChatUI_tsx-userChatWrapper-ugkvugm";
const userChatBubble = "odules__Playground__Components__ChatUI_tsx-userChatBubble-u1fiwp46";

require("./ChatUI.linaria.module.css!=!../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./ChatUI.tsx");